import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, FormField, Input, Label, Spacer } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { HEATING_OIL, UTILITIES_INFORMATION } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { CURRENCY_INPUT_PLACEHOLDER, UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  additionalFields?: object;
};

const INITIAL_STATE = {
  [HEATING_OIL.FIELD_NAME.IS_ENABLED]: true,
  [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER]: null,
  [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
  [HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
};

function HeatingOil_v4({ additionalFields, section, setSectionValues }: Props) {
  const heatingOil_isEnabled = section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED);
  const heatingOil_paysInAdditionIsToggled = section.getFieldValue(
    HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const landlordSelected = section.getFieldValue(HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const fixedAmountValue = section.getFieldValue(HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED),
      ...additionalFields,
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      ...additionalFields,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function onServiceProviderPhoneNumberChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Heating oil"
        subheader="Applies to this property"
        switchOn={heatingOil_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {heatingOil_isEnabled && (
        <>
          <CenteredHR />
          <FormField
            marginBottom="sm"
            label={<Label>What service provider?</Label>}
            control={
              <Input
                placeholder="(example: Holly Heating Oil Co.)"
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>What is the service provider’s number?</Label>}
            control={
              <Input
                type="tel"
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER)}
                onChange={onServiceProviderPhoneNumberChange}
              />
            }
          />
          <SwitchWithTitle
            text="Tenant pays in addition to base rent"
            onClick={togglePaysInAddition}
            toggled={heatingOil_paysInAdditionIsToggled}
          />
          {heatingOil_paysInAdditionIsToggled && (
            <Spacer marginTop="sm">
              <FormField
                label={<Label>Who will be responsible for heating oil set up?</Label>}
                control={
                  <RadioListBox
                    section={section}
                    setSectionValues={setSectionValues}
                    option1FieldName={HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY}
                    option2FieldName={HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                    option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                    option1Content="Responsible for account set up and direct payments to previously specified service provider"
                    option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                    option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                  />
                }
              />
              {landlordSelected && (
                <FormField
                  marginTop="sm"
                  label={<Label>What is the additional fixed fee for heating oil?</Label>}
                  control={
                    <AdornedInput
                      inputId="AdornedInput-fixed-amount-value"
                      leftAdornment={<Adornment>$</Adornment>}
                      rightAdornment={<Adornment>per month</Adornment>}
                      input={
                        <CurrencyInput
                          aria-label="fixed amount"
                          data-test="fixedAmountValue"
                          placeholder={CURRENCY_INPUT_PLACEHOLDER}
                          value={fixedAmountValue || ''}
                          onBlur={setFixedAmountValue}
                          onChange={setFixedAmountValue}
                        />
                      }
                    />
                  }
                />
              )}
            </Spacer>
          )}
        </>
      )}
    </>
  );
}

HeatingOil_v4.description = 'Move service provider to above.';

export default HeatingOil_v4;
